import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Phone,
  ShoppingBag,
  ChevronDown,
  ChevronRight,
  RotateCw,
  AlertTriangle,
  Frown,
  Wallet,
  Package
} from 'lucide-react';
import { format } from 'date-fns';
import { fetchCalls, fetchClientOrders, refreshClientOrders, fetchClientSummary, getTaskAudioUrl } from '../api';
import { formatClassificationType } from '../utils/callClassification';
import { formatCallDirection, formatNegativeSeverity, formatOrderStatus } from '../utils/labels';

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const moscowDateFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: 'long',
  year: 'numeric'
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateFormatter.format(date);
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  return `${number.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`;
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const orderDateKey = (value) => {
  if (!value) return 'unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  // Группируем по московской дате (одна дата — один блок заказа).
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(date);
};

const ClientDetailsModal = ({ client, dateRange, onClose }) => {
  const open = Boolean(client);
  const clientId = client?.clientId;

  const params = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [expandedCalls, setExpandedCalls] = useState(() => new Set());

  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [isRefreshingOrders, setIsRefreshingOrders] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadCalls = useCallback(async () => {
    if (!clientId) return;
    setCallsLoading(true);
    try {
      const response = await fetchCalls({ client_id: clientId, ...params, limit: 200, offset: 0 });
      setCalls(response.items ?? []);
    } catch (err) {
      console.error(err);
      setCalls([]);
    } finally {
      setCallsLoading(false);
    }
  }, [clientId, params]);

  const loadOrders = useCallback(async () => {
    if (!clientId) return;
    setOrdersLoading(true);
    try {
      const response = await fetchClientOrders(clientId, params);
      setOrders(response);
    } catch (err) {
      console.error(err);
      setOrders({ orders: [], lastFetchedAt: null });
    } finally {
      setOrdersLoading(false);
    }
  }, [clientId, params]);

  const loadSummary = useCallback(async () => {
    if (!clientId) return;
    setSummaryLoading(true);
    try {
      const response = await fetchClientSummary(clientId, params);
      setSummary(response);
    } catch (err) {
      console.error(err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId, params]);

  useEffect(() => {
    if (!open) return;
    setExpandedCalls(new Set());
    loadCalls();
    loadOrders();
    loadSummary();
  }, [open, loadCalls, loadOrders, loadSummary]);

  const handleRefreshOrders = useCallback(async () => {
    if (!clientId) return;
    setIsRefreshingOrders(true);
    try {
      const response = await refreshClientOrders(clientId, params);
      setOrders(response);
      loadSummary();
    } catch (err) {
      console.error(err);
    } finally {
      setIsRefreshingOrders(false);
    }
  }, [clientId, params, loadSummary]);

  const toggleCall = useCallback((callId) => {
    setExpandedCalls((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  }, []);

  const groupedOrders = useMemo(() => {
    const list = orders?.orders ?? [];
    const map = new Map();
    list.forEach((order) => {
      const key = orderDateKey(order.orderDate);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(order);
    });
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, items]) => ({ key, items }));
  }, [orders]);

  const clientTitle = client?.name || client?.company || client?.phone || 'Клиент';

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{clientTitle}</DialogTitle>
          <DialogDescription>
            {client?.phone || '—'}
            {client?.company ? ` · ${client.company}` : ''}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="calls" className="w-full">
          <TabsList>
            <TabsTrigger value="calls">Звонки</TabsTrigger>
            <TabsTrigger value="orders">Заказы</TabsTrigger>
            <TabsTrigger value="summary">Сводка</TabsTrigger>
          </TabsList>

          <TabsContent value="calls">
            <CallsTab
              calls={calls}
              loading={callsLoading}
              expandedCalls={expandedCalls}
              onToggle={toggleCall}
            />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersTab
              loading={ordersLoading}
              groupedOrders={groupedOrders}
              lastFetchedAt={orders?.lastFetchedAt}
              isRefreshing={isRefreshingOrders}
              onRefresh={handleRefreshOrders}
            />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab loading={summaryLoading} summary={summary} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const resolveRecordingUrl = (call) => {
  if (!call) return null;
  const trimmed = call.recordingUrl?.trim();
  // Только абсолютный URL используем как есть; относительный /api/tasks/.../audio
  // не несёт токен и базовый адрес API — для него идём через getTaskAudioUrl.
  if (trimmed?.startsWith('http')) return trimmed;
  return getTaskAudioUrl(call.callId);
};

const CallsTab = ({ calls, loading, expandedCalls, onToggle }) => {
  if (loading) {
    return <div className="px-1 py-4 text-sm text-slate-500">Загрузка звонков...</div>;
  }

  if (calls.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Нет звонков за выбранный период.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Тип</TableHead>
            <TableHead>Тема / классификация</TableHead>
            <TableHead>Оператор</TableHead>
            <TableHead>Негатив</TableHead>
            <TableHead>Жалоба</TableHead>
            <TableHead>Подтип</TableHead>
            <TableHead>Дата и время</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.map((call) => {
            const isExpanded = expandedCalls.has(call.callId);
            const Icon = isExpanded ? ChevronDown : ChevronRight;
            const hasNegative = Boolean(call.negativeSeverity) || (call.negativeCategories?.length ?? 0) > 0;

            return (
              <React.Fragment key={call.callId}>
                <TableRow className="cursor-pointer" onClick={() => onToggle(call.callId)}>
                  <TableCell><Icon className="h-4 w-4 text-slate-500" /></TableCell>
                  <TableCell>
                    <div className="inline-flex items-center gap-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm text-slate-700">{formatCallDirection(call.callType)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-700">{call.topic || formatClassificationType(call.callClassificationType)}</div>
                  </TableCell>
                  <TableCell><span className="text-sm text-slate-700">{call.employeeName || '—'}</span></TableCell>
                  <TableCell>
                    {hasNegative ? (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                        {formatNegativeSeverity(call.negativeSeverity) || 'есть'}
                      </Badge>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(call.negativeCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeCategories.map((category, idx) => (
                          <Badge key={idx} variant="outline" className="border-red-200 bg-red-50 text-xs text-red-700">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">
                        {call.callClassificationType === 'delivery_issue' ? 'Подозрение' : '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(call.negativeSubCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeSubCategories.map((subCategory, idx) => (
                          <Badge key={idx} variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-700">
                            {subCategory}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(call.callStartedAt)}</TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-slate-50/70 p-0">
                      <div className="space-y-4 px-4 py-4">
                        <audio className="w-full" src={resolveRecordingUrl(call)} controls />
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                          <div className="mb-2 text-sm font-semibold text-slate-900">Транскрипция</div>
                          {call.transcript ? (
                            <pre className="whitespace-pre-wrap break-words text-sm text-slate-700">{call.transcript}</pre>
                          ) : (
                            <div className="text-sm text-slate-500">Транскрипция недоступна.</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const OrdersTab = ({ loading, groupedOrders, lastFetchedAt, isRefreshing, onRefresh }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <ShoppingBag className="h-4 w-4" />
          Заказы клиента
        </div>
        <div className="flex items-center gap-3">
          {lastFetchedAt && (
            <span className="text-xs text-slate-400">Обновлено {formatDateTime(lastFetchedAt)}</span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
            disabled={isRefreshing}
            onClick={onRefresh}
          >
            <RotateCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Обновляем...' : 'Обновить из iiko'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="px-1 py-4 text-sm text-slate-500">Загрузка заказов...</div>
      ) : groupedOrders.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Заказов не найдено. Нажмите «Обновить из iiko», чтобы запросить актуальные данные.
        </div>
      ) : (
        <div className="space-y-4">
          {groupedOrders.map(({ key, items }) => (
            <div key={key} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-sm font-semibold text-slate-900">
                {items[0]?.orderDate ? formatDate(items[0].orderDate) : 'Дата неизвестна'}
              </div>
              <div className="space-y-3">
                {items.map((order) => (
                  <OrderCard key={order.orderNumber} order={order} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OrderCard = ({ order }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
    <div className="flex items-center justify-between">
      <div className="font-medium text-slate-900">
        Заказ #{order.orderNumber}
        {order.status ? ` · ${formatOrderStatus(order.status)}` : ''}
      </div>
      <div className="font-semibold text-slate-900">{formatMoney(order.total)}</div>
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      <div className="text-slate-500">Время</div>
      <div>{formatDateTime(order.orderDate)}</div>
      <div className="text-slate-500">Адрес</div>
      <div>{order.address || '—'}</div>
      {order.comment && (
        <>
          <div className="text-slate-500">Комментарий</div>
          <div className="italic">{order.comment}</div>
        </>
      )}
      {order.cancelCauseName && (
        <>
          <div className="text-red-600">Причина отмены</div>
          <div className="text-red-700">{order.cancelCauseName}</div>
        </>
      )}
    </div>
    {Array.isArray(order.items) && order.items.length > 0 && (
      <div className="mt-1">
        <div className="mb-1 font-medium text-slate-800">Состав</div>
        <div className="space-y-1">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="flex-1">
                {item.name}
                {item.amount !== 1 && ` × ${item.amount}`}
                {item.comment && <span className="ml-1 italic text-slate-400">({item.comment})</span>}
              </span>
              <span className="shrink-0 text-slate-500">{item.price} ₽</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const SummaryTab = ({ loading, summary }) => {
  if (loading) {
    return <div className="px-1 py-4 text-sm text-slate-500">Загрузка сводки...</div>;
  }

  if (!summary) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Нет данных по клиенту.
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <SummaryBlock title="За выбранный период" block={summary.period} />
      <SummaryBlock title="За всё время" block={summary.lifetime} />
    </div>
  );
};

const SummaryBlock = ({ title, block }) => {
  const data = block ?? {};
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        <SummaryMetric
          icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
          label="Жалобы"
          value={data.hasComplaints ? `Да (${data.complaintsCount})` : 'Нет'}
          danger={data.hasComplaints}
        />
        <SummaryMetric
          icon={<Frown className="h-4 w-4 text-red-600" />}
          label="Негатив"
          value={data.negativeCount ?? 0}
          danger={(data.negativeCount ?? 0) > 0}
        />
        <SummaryMetric
          icon={<Wallet className="h-4 w-4 text-emerald-600" />}
          label="Средний чек"
          value={formatMoney(data.avgCheck)}
        />
        <SummaryMetric
          icon={<Package className="h-4 w-4 text-sky-600" />}
          label="Заказов"
          value={data.ordersCount ?? 0}
        />
        <SummaryMetric
          icon={<Phone className="h-4 w-4 text-slate-500" />}
          label="Звонков"
          value={data.callsCount ?? 0}
        />
        <SummaryMetric
          icon={<Wallet className="h-4 w-4 text-slate-500" />}
          label="Сумма заказов"
          value={formatMoney(data.totalSpent)}
        />
      </div>
    </div>
  );
};

const SummaryMetric = ({ icon, label, value, danger = false }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
    <div className="flex items-center gap-2 text-xs text-slate-500">
      {icon}
      {label}
    </div>
    <div className={`mt-1 text-lg font-semibold ${danger ? 'text-red-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

export default ClientDetailsModal;
